/**
 * Composant NotificationBell
 * 
 * Affiche une cloche de notification avec badge dans le header du backoffice.
 * Permet de voir et gérer les notifications en temps réel.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X, Check, Trash2, CheckCheck, AlertTriangle, Calendar, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationService, Notification } from '../../services/notificationService';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNotificationSSE } from '../../hooks/useNotificationSSE';

export const NotificationBell: React.FC = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // Callback pour les mises à jour SSE
  const handleSSEUnreadCountChange = useCallback((count: number) => {
    setUnreadCount(count);
  }, []);

  // Connexion SSE pour les notifications en temps réel
  const { isConnected } = useNotificationSSE(handleSSEUnreadCountChange);

  // Charger le compteur de notifications non lues (fallback si SSE non connecté)
  const loadUnreadCount = useCallback(async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Erreur chargement compteur:', error);
    }
  }, []);

  // Charger les notifications
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationService.getNotifications({
        unreadOnly: filter === 'unread',
        limit: 50
      });
      setNotifications(data);
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Charger le compteur initial
  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  // Charger les notifications quand on ouvre le panel
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, filter, loadNotifications]);

  // Ouvrir/fermer le panel
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Marquer une notification comme lue
  const handleMarkAsRead = async (id: number) => {
    try {
      await notificationService.markAsRead(id);
      
      // Si on est en mode "Non lues", retirer la notification de la liste
      if (filter === 'unread') {
        setNotifications(notifications.filter(n => n.id !== id));
      } else {
        // Sinon, juste mettre à jour le statut
        setNotifications(notifications.map(n => 
          n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        ));
      }
      
      // Décrémenter le compteur
      if (unreadCount > 0) {
        setUnreadCount(unreadCount - 1);
      }
    } catch (error) {
      console.error('Erreur marquage:', error);
    }
  };

  // Marquer toutes comme lues
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Mettre à jour l'état local
      setNotifications(notifications.map(n => ({
        ...n,
        is_read: true,
        read_at: new Date().toISOString()
      })));
      
      setUnreadCount(0);
    } catch (error) {
      console.error('Erreur marquage global:', error);
    }
  };

  // Supprimer une notification
  const handleDelete = async (id: number) => {
    try {
      await notificationService.deleteNotification(id);
      
      // Retirer de la liste
      const notif = notifications.find(n => n.id === id);
      setNotifications(notifications.filter(n => n.id !== id));
      
      // Décrémenter le compteur si non lue
      if (notif && !notif.is_read && unreadCount > 0) {
        setUnreadCount(unreadCount - 1);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  // Obtenir l'icône selon le type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_booking':
        return <Calendar className="w-5 h-5 text-blue-600" />;
      case 'payment_failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'payment_reversed':
        return <AlertTriangle className="w-5 h-5 text-red-600 animate-pulse" />;
      case 'booking_canceled':
        return <XCircle className="w-5 h-5 text-orange-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  // Obtenir la classe CSS selon la priorité
  const getPriorityClass = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'border-l-4 border-red-600 bg-red-50';
      case 'high':
        return 'border-l-4 border-orange-500 bg-orange-50';
      case 'medium':
        return 'border-l-4 border-blue-500 bg-blue-50';
      default:
        return 'border-l-4 border-gray-400 bg-white';
    }
  };

  return (
    <div className="relative">
      {/* Bouton cloche */}
      <button
        onClick={handleToggle}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
        {/* Indicateur de connexion SSE */}
        {isConnected && (
          <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-white" title="Temps réel actif"></span>
        )}
      </button>

      {/* Panel des notifications */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay avec flou pour fermer en cliquant à l'extérieur */}
            <div
              className="fixed inset-0 z-40 bg-black/10 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed top-16 right-4 lg:top-4 lg:left-80 w-96 max-w-[calc(100vw-2rem)] bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.25)] border border-white/40 z-[100] max-h-[600px] flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 border-b border-white/40 bg-gradient-to-r from-pink-50/90 to-purple-50/90">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900 text-lg">Notifications</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 hover:bg-white rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Filtres et actions */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setFilter('all')}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        filter === 'all'
                          ? 'bg-white text-pink-600 shadow-sm'
                          : 'text-gray-600 hover:bg-white/50'
                      }`}
                    >
                      Toutes
                    </button>
                    <button
                      onClick={() => setFilter('unread')}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        filter === 'unread'
                          ? 'bg-white text-pink-600 shadow-sm'
                          : 'text-gray-600 hover:bg-white/50'
                      }`}
                    >
                      Non lues {unreadCount > 0 && `(${unreadCount})`}
                    </button>
                  </div>

                  {notifications.length > 0 && unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-white rounded-lg transition-colors"
                      title="Tout marquer comme lu"
                    >
                      <CheckCheck className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Liste des notifications */}
              <div className="overflow-y-auto flex-1">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600 mx-auto"></div>
                    <p className="text-gray-500 text-sm mt-2">Chargement...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">
                      {filter === 'unread' ? 'Aucune notification non lue' : 'Aucune notification'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {notifications.map((notif) => (
                      <motion.div
                        key={notif.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={`relative p-4 transition-all duration-200 ${getPriorityClass(notif.priority)} ${
                          !notif.is_read
                            ? 'bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 shadow-[0_0_20px_rgba(59,130,246,0.4)] ring-2 ring-blue-400 scale-[1.02] font-medium'
                            : 'bg-white hover:bg-gray-50 opacity-70'
                        }`}
                      >
                        {!notif.is_read && (
                          <span className="absolute -left-0.5 top-0 bottom-0 w-1.5 rounded-full bg-gradient-to-b from-blue-500 to-blue-600 shadow-lg"></span>
                        )}
                        <div className="flex items-start gap-3">
                          {/* Icône */}
                          <div className="flex-shrink-0 mt-0.5">
                            {getNotificationIcon(notif.type)}
                          </div>

                          {/* Contenu */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h4 className={`font-semibold text-sm leading-tight ${
                                notif.is_read ? 'text-gray-600' : 'text-blue-800'
                              }`}>
                                {!notif.is_read && <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>}
                                {notif.title}
                              </h4>
                              {notif.priority === 'critical' && (
                                <span className="flex-shrink-0 text-red-600 text-xs font-bold px-2 py-0.5 bg-red-100 rounded">
                                  URGENT
                                </span>
                              )}
                            </div>
                            
                            <p className={`text-sm mb-2 leading-relaxed ${
                              notif.is_read ? 'text-gray-500' : 'text-gray-700'
                            }`}>
                              {notif.message}
                            </p>

                            {/* Métadonnées supplémentaires */}
                            {notif.extra_data && (
                              <div className="text-xs text-gray-500 space-y-0.5 mb-2">
                                {notif.extra_data.has_voucher && (
                                  <div className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-md font-medium mb-1">
                                    🎁 Bon cadeau utilisé
                                  </div>
                                )}
                                {notif.extra_data.practitioners && notif.extra_data.practitioners.length > 0 && (
                                  <div className="font-medium text-pink-600">
                                    👤 {notif.extra_data.practitioners.join(', ')}
                                  </div>
                                )}
                                {notif.extra_data.start_datetime_formatted && (
                                  <div className="text-blue-600">📅 {notif.extra_data.start_datetime_formatted}</div>
                                )}
                              </div>
                            )}

                            {/* Footer */}
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-400">
                                {formatDistanceToNow(new Date(notif.created_at), {
                                  addSuffix: true,
                                  locale: fr
                                })}
                              </p>

                              <div className="flex items-center gap-2">
                                {!notif.is_read && (
                                  <button
                                    onClick={() => handleMarkAsRead(notif.id)}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-sm hover:shadow-md"
                                    title="Marquer comme lu"
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">Lu</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDelete(notif.id)}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-white hover:bg-red-600 border border-gray-300 hover:border-red-600 rounded-lg transition-all"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span className="hidden sm:inline">Suppr.</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
